import { getAndUpdateDvid } from 'MMT-UI/helpers/client/dvid/index.js';
import { USER_TYPE_AGENT } from '../constants/appConstant';
import { getCookie, setCookie } from '../service/cookieService';
import { SHOW_DIY_CARD, DATE_FORMAT, MONTHS_CAP, DAYS } from '../src/constants';
import { getAuthToken, isHEMmtDomain, isMmtDomain } from './appUtils';
import { getUserInfoFromServer } from './getUserInfo';

let paramsMap = {};

export const getUrlParamsMap = () => {
  if (ssr.isOnClient && isEmpty(paramsMap)) {
    const params = window.location.search && window.location.search.substring(1);
    if (params && params.length) {
      const paramVars = params.split('&');
      for (const param of paramVars) {
        const pair = param.split('=');
        paramsMap[pair[0].toLowerCase()] = decodeURIComponent(pair[1]);
      }
    }
  } else if (!ssr.isOnClient && isEmpty(paramsMap)) {
    paramsMap = ssr.queryParams;
  }
  return paramsMap;
};

export const getUrlParamsMapV2 = (url = '') => {
  let paramsMap = {};
  if (ssr.isOnClient && isEmpty(paramsMap)) {
    const params = url || (window.location.search && window.location.search.substring(1));
    if (params && params.length) {
      const paramVars = params.split('&');
      for (const param of paramVars) {
        const pair = param.split('=');
        paramsMap[pair[0]] = decodeURIComponent(pair[1]);
      }
    }
  } else if (!ssr.isOnClient && isEmpty(paramsMap)) {
    paramsMap = ssr.requestBody || {};
  }
  return paramsMap;
};

function s4() {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
}

export const getUUID = () => `${s4() + s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;

export const isUserLoggedIn = () => !!getAuthToken();

export const getQueryStr = params =>
  Object.keys(params)
    .filter(key => params[key])
    .map(key => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');

function debouce(fn, wait, immediate) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) fn.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) fn.apply(context, args);
  };
}

export const getDvid = () => {
  let dvid = getAndUpdateDvid();
  if (dvid === undefined) {
    dvid = getUUID();
    setCookie('dvid', dvid, { maxAge: 365, domain: '.makemytrip.com' });
  }
  return dvid;
};

export const reload = () => {
  window.location.reload(true);
};

export const getOmniture = () => {
  let s = {};
  if (ssr.isOnClient && typeof window.s_gi === 'function') {
    s = window.s_gi('mmtprod');
  }
  return s;
};

const isEmpty = obj => !!obj && !Object.keys(obj).length;

export const dateFormatter = (date, type) => {
  const year = date.getFullYear();
  let month = date.getMonth() + 1;
  month = month < 10 ? `0${month}` : month;
  let day = date.getDate();
  day = day < 10 ? `0${day}` : day;
  const dayOfWeek = DAYS[date.getDay()];

  switch (type) {
    case DATE_FORMAT.YYYY_MM_DD:
      return `${year}-${month}-${day}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case DATE_FORMAT.DAY_MMM_DD_YYYY: // Thu, Apr 16, 2020
      return `${dayOfWeek}, ${MONTHS_CAP[date.getMonth()]} ${day}, ${year}`;
    case DATE_FORMAT.DAY_MMM_DD: // Thu, Apr 16
      return `${dayOfWeek}, ${MONTHS_CAP[date.getMonth()]} ${day}`;
    default:
      return `${year}-${month}-${day}`;
  }
};

export const getHostName = () => {
  if (ssr.isOnClient) {
    const url = new URL(window.location);
    return url.hostname;
  }
  return ssr.host;
};

function getUniqueKey() {
  return s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4();
}

export const getSessionStorage = name =>
  (ssr.isOnClient ? sessionStorage.getItem(name) : undefined);

export const getLocalStorage = name => (ssr.isOnClient ? localStorage.getItem(name) : null);

export const setSessionStorage = (name, value) => {
  if (ssr.isOnClient) {
    sessionStorage.setItem(name, value);
  }
};

export const setLocalStorage = (name, value) => {
  if (ssr.isOnClient) {
    localStorage.setItem(name, value);
  }
};

export const removeLocalStorage = name => {
  if (ssr.isOnClient) {
    localStorage.removeItem(name);
  }
};

const getOSName = () => {
  const headers = {
    os: 'unknown',
    osver: 'unknown',
  };
  const userAgent = ssr.isOnClient ? window.navigator.userAgent : ssr.userAgent;
  if (userAgent.indexOf('Windows NT') != -1) {
    headers.os = 'Windows';
    if (userAgent.indexOf('Windows NT 10.0') != -1) {
      headers.osver = 'Windows 10';
    } else if (userAgent.indexOf('Windows NT 6.2') != -1) {
      headers.osver = 'Windows 8';
    } else if (userAgent.indexOf('Windows NT 6.1') != -1) {
      headers.osver = 'Windows 7';
    } else if (userAgent.indexOf('Windows NT 6.0') != -1) {
      headers.osver = 'Windows Vista';
    } else if (userAgent.indexOf('Windows NT 5.1') != -1) {
      headers.osver = 'Windows XP';
    } else if (userAgent.indexOf('Windows NT 5.0') != -1) {
      headers.osver = 'Windows 2000';
    }
  } else if (userAgent.indexOf('Mac') != -1) {
    headers.osver = 'Mac/iOS';
    headers.os = 'Mac';
  } else if (userAgent.indexOf('Linux') != -1) {
    headers.osver = 'Linux';
    headers.os = 'Linux';
  }
  return headers;
};

const triggerScrollEventWithoutScroll = () => {
  if (ssr.isOnClient) {
    window.scrollTo(window.scrollX, window.scrollY + 1);
    window.scrollTo(window.scrollX, window.scrollY - 1);
  }
};

export const isAgentLoggedIn = () => {
  let isAgent = false;
  const isMmt = isMmtDomain() || isHEMmtDomain();
  const user = getUserInfoFromServer();
  if (ssr.isOnClient && isMmt && user) {
    if (user.profileType && user.profileType === USER_TYPE_AGENT) {
      isAgent = true;
    }
  }

  return isAgent;
};

const getDataKey = () => {
  const dkParam = getUrlParamsMap().dk;
  if (dkParam) {
    return dkParam;
  }
  return false;
};
const getPackageDetailURL = () => {
  const queryParams = getUrlParamsMapV2();
  const updatedQueryparam = { ...queryParams, story: true };
  const detailPageURL = `${window.location.origin}${window.location.pathname}?${getQueryStr(
    updatedQueryparam
  )}`;
  return detailPageURL;
};
const removeStoryFromLocalStorage = () => {
  const queryParams = getUrlParamsMapV2();
  if (queryParams.id) {
    const localStorageKey = `story_${queryParams.id}`;
    if (window.localStorage.getItem(localStorageKey) !== null) {
      window.localStorage.removeItem(localStorageKey);
    }
  }
};
const easeInOutQuad = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return (c / 2) * t * t + b;
  t--;
  return (-c / 2) * (t * (t - 2) - 1) + b;
};
export const gotoLocation = (elementSelector, scrollAmount = 170) => {
  // Firefox 1.0+
  const isFirefox = typeof InstallTrigger !== 'undefined';

  // Internet Explorer 6-11
  const isIE = /* @cc_on!@ */ false || !!document.documentMode;

  // Edge 20+
  const isEdge = !isIE && !!window.StyleMedia;

  // Chrome 1+
  const isChrome = !!window.chrome;

  const targetElement = document.querySelector(elementSelector);
  if (!targetElement) return;

  const target = targetElement.offsetTop - scrollAmount;

  if (isChrome || isFirefox) {
    scrollTo(document.documentElement, target, 500);
  } else {
    scrollTo(document.body, target, 500);
  }

  function scrollTo(element, to, duration) {
    const scrollTop = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    );
    const start = scrollTop;
    const change = to - start;
    let currentTime = 0;
    const increment = 20;

    const animateScroll = () => {
      currentTime += increment;
      const val = easeInOutQuad(currentTime, start, change, duration);
      element.scrollTop = val;

      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };
    animateScroll();
  }
};
export const isSavedPackageUrl = () => {
  const locationSplit = window.location.pathname.split('/');
  if (locationSplit.length > 3) {
    return locationSplit[3] === 'savePackage';
  }
  return false;
};
export const isFromAmendment = () => getUrlParamsMapV2().fromAmendment;

export const isFromCustomerAmendment = () => getUrlParamsMapV2().componentAmendment;
export const getBaseAPI = () => (isFromAmendment() ? '/amendment/dateChange' : '/package');

export const getAddedDate = days => {
  const result = new Date();
  result.setDate(result.getDate() + days);
  return dateFormatter(result, 'YYYY-MM-DD');
};
const posDIYCard = () => MMT_HLD && MMT_HLD.posDIYCard;
const isDIYStrip = () => MMT_HLD && MMT_HLD.showDIYCard === SHOW_DIY_CARD.STRIP;

const isDIYCard = () => MMT_HLD && MMT_HLD.showDIYCard === SHOW_DIY_CARD.CARD;
export const isDiyPlannerDetails = () => {
  const itineraryId = getUrlParamsMapV2().itineraryId || getUrlParamsMapV2().diyItineraryId;
  if (itineraryId) {
    return true;
  }
  return false;
};

export const getUrlWithParams = (params = {}, path = '') => {
  const currentUrl = `${window.location.origin}${path || window.location.pathname}`;
  const queryString = Object.keys(params)
    .map(key => `${key}=${params[key]}`)
    .join('&');

  if (queryString) {
    return `${currentUrl}?${queryString}`;
  }
  return currentUrl;
};

const changeFavicon = icon => {
  if (icon) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = icon;
  }
};

export const capitalize = string => string?.charAt(0)?.toUpperCase() + string?.slice(1);

export const changeWindowUrl = (newParamList, refreshPage = false) => {
  const oldParams = getUrlParamsMapV2();

  if (oldParams.fromCity !== newParamList.fromCity && oldParams.variantId) {
    delete oldParams.variantId;
  }
  const newUrl = getUrlWithParams({ ...oldParams, ...newParamList });
  if (refreshPage) {
    window.location.href = newUrl;
  } else {
    window.history.pushState(newUrl, '', newUrl);
  }
};

export const loadJS = (fileURL, { onLoad, onError }) => {
  const scriptEle = document.createElement('script');
  scriptEle.setAttribute('src', fileURL);
  document.body.appendChild(scriptEle);

  scriptEle.addEventListener('load', () => {
    onLoad?.();
  });

  scriptEle.addEventListener('error', ev => {
    onError?.(ev);
  });
};

export const getDaysDiff = date => Math.floor((date - Date.now()) / (1000 * 60 * 60 * 24));

export const isPreviewDetail = () =>
  (ssr.isOnClient && window?.location?.href?.includes('previewDetail')) || false;

export const isMobile = () =>
  (ssr.isOnClient ? /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) : ssr.isMobile);

export const formatURLDate = date => new Date(`${date}`.split('/').reverse().join('-'));

export const isValidDate = date => {
  if (date) {
    return !date.split('/').some(part => isNaN(+part));
  }

  return false;
};

export const debounce = (func, delay) => {
  let inDebounce;
  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(inDebounce);
    inDebounce = setTimeout(() => func.apply(context, args), delay);
  };
};

export const getIframeValue = () => {
  const params = getUrlParamsMapV2() || {};
  if (params?.isIframe === 'true') {
    return true;
  }
  if (params?.isIframe === 'false') {
    return false;
  }
  return false; // default false ; currently true due to mydesk changes
};

export default {
  getUrlParamsMap,
  getUUID,
  isUserLoggedIn,
  getQueryStr,
  debouce,
  reload,
  isEmpty,
  dateFormatter,
  getHostName,
  getOmniture,
  getUniqueKey,
  getSessionStorage,
  setSessionStorage,
  getOSName,
  getUrlParamsMapV2,
  triggerScrollEventWithoutScroll,
  isAgentLoggedIn,
  removeStoryFromLocalStorage,
  getPackageDetailURL,
  gotoLocation,
  getDataKey,
  getDvid,
  isSavedPackageUrl,
  isFromAmendment,
  isFromCustomerAmendment,
  getBaseAPI,
  getAddedDate,
  posDIYCard,
  isDIYStrip,
  isDIYCard,
  isDiyPlannerDetails,
  getUrlWithParams,
  changeFavicon,
  changeWindowUrl,
  isPreviewDetail,
  getIframeValue
};

export { getCookie };